import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ConsultationPopup from '../../components/feature/ConsultationPopup';

export default function ServicesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-book-open-line',
      title: 'Academic Support',
      description: 'Comprehensive guidance to help students excel in their studies and achieve academic success.',
      color: 'from-[#14B8A6] to-[#0D9488]',
      details: [
        'Study strategies and time management',
        'Exam preparation and revision techniques',
        'Essay writing and research skills',
        'Academic planning and goal setting',
        'Subject-specific tutoring support'
      ],
      image: 'https://public.readdy.ai/ai/img_res/461c20fab2038c9e06f50de4275b43cb.jpg'
    },
    {
      icon: 'ri-compass-line',
      title: 'Career Guidance',
      description: 'Navigate your career path with confidence through personalized planning and professional development.',
      color: 'from-[#0D9488] to-[#0F766E]',
      details: [
        'Career exploration and planning',
        'CV and cover letter writing',
        'Interview preparation and practice',
        'LinkedIn profile optimization',
        'Job search strategies and networking'
      ],
      image: 'https://readdy.ai/api/search-image?query=Career%20counseling%20session%20with%20young%20professional%2C%20mentor%20helping%20with%20career%20planning%2C%20modern%20office%20setting%20with%20laptop%20and%20documents%2C%20supportive%20guidance%20atmosphere%2C%20teal%20accents%2C%20professional%20yet%20approachable%20environment&width=600&height=400&seq=service-career-1&orientation=landscape'
    },
    {
      icon: 'ri-mental-health-line',
      title: 'Personal Development',
      description: 'Build confidence, set meaningful goals, and develop the skills to thrive in all areas of life.',
      color: 'from-[#0F766E] to-[#115E59]',
      details: [
        'Life coaching and goal setting',
        'Confidence and self-esteem building',
        'Stress management techniques',
        'Work-life balance strategies',
        'Personal growth planning'
      ],
      image: 'https://readdy.ai/api/search-image?query=Personal%20development%20coaching%20session%2C%20supportive%20life%20coach%20with%20client%2C%20comfortable%20modern%20counseling%20room%2C%20empowering%20and%20positive%20atmosphere%2C%20natural%20lighting%20with%20teal%20color%20accents%2C%20growth%20mindset%20environment&width=600&height=400&seq=service-personal-1&orientation=landscape'
    },
    {
      icon: 'ri-team-line',
      title: 'Relationship Support',
      description: 'Improve communication skills and build healthier, more fulfilling relationships.',
      color: 'from-[#14B8A6] to-[#0D9488]',
      details: [
        'Communication skills development',
        'Conflict resolution strategies',
        'Family relationship guidance',
        'Friendship and social skills',
        'Boundary setting and self-care'
      ],
      image: 'https://readdy.ai/api/search-image?query=Supportive%20counseling%20session%20for%20relationships%2C%20empathetic%20therapist%20with%20clients%2C%20warm%20and%20safe%20counseling%20environment%2C%20comfortable%20modern%20office%2C%20caring%20atmosphere%20with%20teal%20accents%2C%20inclusive%20and%20welcoming%20setting&width=600&height=400&seq=service-relationship-1&orientation=landscape'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Startup Mentorship',
      description: 'Turn your business idea into reality with expert guidance and practical startup support.',
      color: 'from-[#0D9488] to-[#0F766E]',
      details: [
        'Business idea validation and planning',
        'Business model development',
        'Pitch deck creation and refinement',
        'Funding strategy and investor readiness',
        'Growth planning and scaling strategies'
      ],
      image: 'https://readdy.ai/api/search-image?query=Startup%20mentorship%20session%20with%20young%20entrepreneurs%2C%20business%20mentor%20helping%20founders%20with%20strategy%2C%20modern%20collaborative%20workspace%2C%20innovative%20and%20energetic%20atmosphere%2C%20teal%20color%20scheme%2C%20supportive%20startup%20environment&width=600&height=400&seq=service-startup-1&orientation=landscape'
    },
    {
      icon: 'ri-funds-line',
      title: 'Financial Planning',
      description: 'Learn to manage your finances effectively and build a secure financial future.',
      color: 'from-[#0F766E] to-[#115E59]',
      details: [
        'Personal budgeting and money management',
        'Student loan and debt management',
        'Savings and investment basics',
        'Financial goal setting',
        'Startup funding guidance'
      ],
      image: 'https://readdy.ai/api/search-image?query=Financial%20planning%20consultation%20with%20young%20person%2C%20friendly%20financial%20advisor%20explaining%20budgeting%2C%20modern%20office%20with%20laptop%20and%20financial%20documents%2C%20approachable%20and%20educational%20atmosphere%2C%20teal%20accents%2C%20supportive%20learning%20environment&width=600&height=400&seq=service-financial-1&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/fa77cf8cc3bbd77c4d454e1d51d6ad11/4b0a760ad430598ce13c316b2b8752fe.png" alt="A2Z Consulting Services" className="h-12 w-auto" />
            <span className="text-xl font-semibold text-[#001F3F]">A2Z Consulting Services</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Home</Link>
            <Link to="/about" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">About</Link>
            <Link to="/services" className="text-sm font-medium text-[#14B8A6] transition-colors border-b-2 border-[#14B8A6]">Services</Link>
            <Link to="/team" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Our Team</Link>
            <Link to="/case-studies" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Success Stories</Link>
            <Link to="/blog" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Resources</Link>
            <Link to="/contact" className="text-sm font-medium text-[#001F3F] transition-colors hover:text-[#14B8A6]">Contact</Link>
          </div>
          <Link to="/contact" className="hidden md:block px-6 py-2.5 rounded-full bg-[#14B8A6] text-white text-sm font-medium transition-all hover:bg-[#0D9488] hover:scale-105 whitespace-nowrap">
            Get Support
          </Link>
        </div>
      </nav>

      {/* Hero Banner */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#14B8A6]/10 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="text-[#14B8A6] hover:underline">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Services</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-[#001F3F] mb-6">Our Services</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Comprehensive support tailored to your unique needs and goals
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#14B8A6]/30 cursor-pointer hover:-translate-y-2">
                <div className="relative h-64 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                </div>
                <div className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#001F3F] mb-4">{service.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  <button 
                    onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                    className="text-[#14B8A6] font-semibold flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    Learn More <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Details Accordion */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-[#001F3F] text-center mb-16">What's Included</h2>
          <div className="space-y-4">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-md">
                <button
                  onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${service.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#001F3F]">{service.title}</h3>
                  </div>
                  <i className={`ri-${openAccordion === index ? 'subtract' : 'add'}-line text-2xl text-[#14B8A6] transition-transform`}></i>
                </button>
                <div className={`transition-all duration-300 overflow-hidden ${openAccordion === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-8 pb-6">
                    <p className="text-base text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    <ul className="space-y-3">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                          <i className="ri-check-line text-[#14B8A6] text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={() => setIsPopupOpen(true)}
                      className="inline-flex items-center gap-2 mt-8 px-8 py-3 bg-[#14B8A6] text-white rounded-full text-sm font-semibold transition-all hover:scale-105 hover:bg-[#0D9488] whitespace-nowrap cursor-pointer"
                    >
                      Book a Session <i className="ri-calendar-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#14B8A6] to-[#0D9488]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-lg text-white/90 mb-10 leading-relaxed">
            Book a free consultation and discover how we can support you in achieving your goals.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => setIsPopupOpen(true)} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#14B8A6] rounded-full font-bold text-base transition-all hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer">
              Book Free Consultation <i className="ri-calendar-line"></i>
            </button>
            <a href="https://wa.me/447494699771" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-10 py-4 border-2 border-white text-white rounded-full font-bold text-base transition-all hover:scale-105 hover:bg-white hover:text-[#14B8A6] whitespace-nowrap">
              <i className="ri-whatsapp-line text-xl"></i> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] rounded-t-3xl pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/fa77cf8cc3bbd77c4d454e1d51d6ad11/4b0a760ad430598ce13c316b2b8752fe.png" alt="A2Z" className="h-10 w-auto" />
                <span className="text-xl font-semibold text-white">A2Z Consulting Services</span>
              </div>
              <p className="text-sm text-gray-400 mb-8">Empowering every step of your journey</p>
              <h4 className="text-lg font-bold text-white mb-3">Stay Updated</h4>
              <p className="text-sm text-gray-400 mb-4">Get the latest insights and news</p>
              <div className="flex flex-col gap-3">
                <input type="email" placeholder="Enter your email" className="w-full px-4 py-3 bg-[#2A2A2A] text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/20" />
                <button className="px-7 py-3 bg-white text-black rounded-full text-sm font-medium transition-all hover:scale-105 whitespace-nowrap">
                  Subscribe <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Quick Links</h4>
              <div className="flex flex-col gap-4">
                <Link to="/" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Home</Link>
                <Link to="/about" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">About Us</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Services</Link>
                <Link to="/team" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Our Team</Link>
                <Link to="/case-studies" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Success Stories</Link>
                <Link to="/blog" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Resources</Link>
                <Link to="/pricing" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Pricing</Link>
                <Link to="/faq" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">FAQ</Link>
                <Link to="/contact" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Our Services</h4>
              <div className="flex flex-col gap-4">
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Academic Support</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Career Guidance</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Personal Development</Link>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Startup Mentorship</Link>
              </div>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Contact</h4>
              <div className="flex flex-col gap-4 mb-8">
                <a href="tel:+447494699771" className="text-base text-white hover:underline">+44 7494 699 771</a>
                <a href="mailto:hello@a2zconsultingservices.com" className="text-base text-white hover:underline">hello@a2zconsultingservices.com</a>
                <a href="https://wa.me/447494699771" target="_blank" rel="noopener noreferrer" className="text-base text-white hover:underline flex items-center gap-2">
                  <i className="ri-whatsapp-line"></i> WhatsApp Us
                </a>
              </div>
              <h5 className="text-sm text-gray-400 mb-4">Follow Us</h5>
              <p className="text-sm text-gray-400 leading-relaxed">123 Business Avenue<br />Manchester, M1 1AA, UK</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">LinkedIn</a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Twitter</a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Facebook</a>
            </div>
            <p className="text-sm text-gray-400 text-center">© 2025 A2Z Consulting Services. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      <ConsultationPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
}
